import { tWapiMedia } from 'src/components/Chat/Chat.types.ts';

/**
 * Conservative cap (in code points) under Meta's documented document.filename
 * limit. Oversized names are truncated with the extension preserved so the
 * document title degrades instead of failing the WhatsApp send (BUG-475).
 *
 * Mirrors `MaxDocumentFilenameLen` in the WBS backend
 * (`internal/messaging/application/common/pii.go`). WBS is the authoritative
 * guard: it additionally strips control / bidi / zero-width characters, which
 * this frontend deliberately does not (a browser `File.name` never contains
 * them). Keep the 240 cap in sync if Meta's limit ever changes.
 */
export const MAX_DOCUMENT_FILENAME_LENGTH = 240;

/**
 * Truncates a filename to `maxLength` code points while preserving a short file
 * extension. Operates on code points (via Array.from) so a multi-byte character
 * is never split into invalid UTF-16.
 */
export function truncateFilename(name: string, maxLength: number): string {
  const chars = Array.from(name);
  if (chars.length <= maxLength) return name;

  const dot = name.lastIndexOf('.');
  const ext =
    dot >= 0 && Array.from(name.slice(dot)).length <= 12 ? name.slice(dot) : '';
  const extLength = Array.from(ext).length;
  if (extLength >= maxLength) return chars.slice(0, maxLength).join('');
  return chars.slice(0, maxLength - extLength).join('') + ext;
}

/**
 * Builds the media object for a WAPI template header send. The original
 * filename is attached only for document headers (WBS ignores it for
 * image/video) and capped to a Meta-safe length, so the recipient sees the real
 * document name instead of "Sin título" (BUG-475).
 */
export function buildTemplateMedia(params: {
  mediaType: 'image' | 'video' | 'document';
  url: string;
  fileName: string;
}): tWapiMedia {
  const { mediaType, url, fileName } = params;
  const media: tWapiMedia = { type: mediaType, url };
  const trimmedFileName = fileName.trim();
  if (mediaType === 'document' && trimmedFileName !== '') {
    media.filename = truncateFilename(
      trimmedFileName,
      MAX_DOCUMENT_FILENAME_LENGTH,
    );
  }
  return media;
}
